const TOKEN_KEY = 'sanjeom_ai_auth_token';

function apiBase(): string {
  return String(import.meta.env.VITE_API_URL || 'http://127.0.0.1:8102/api/v1').trim().replace(/\/$/, '');
}

export class ApiError extends Error {
  status: number;
  code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export type AccountProfile = {
  id: number;
  email?: string | null;
  display_name?: string | null;
  username?: string | null;
};

export const CURRENT_AI_DATA_SHARING_VERSION = '2026-09-13-ai-providers-v2';

type AuthResponse = {
  access_token?: unknown;
  user?: AccountProfile;
};

export type ConversationMessage = {
  role: 'user' | 'assistant';
  content: string;
  image_url?: string | null;
  image_mime_type?: string | null;
  created_at: string;
};

export type ActiveTurn = {
  turn_id: string;
  status: 'queued' | 'running';
  message: string;
  updated_at?: string | null;
};

export type Conversation = {
  session_id: string;
  session_ref?: string | null;
  title?: string | null;
  status: string;
  messages: ConversationMessage[];
  active_turn?: ActiveTurn | null;
  created_at: string;
  updated_at: string;
};

export type ConversationSummary = {
  session_id: string;
  session_ref?: string | null;
  title?: string | null;
  status: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TurnStatus = {
  session_id: string;
  session_ref?: string | null;
  turn_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  messages: ConversationMessage[];
  error_message?: string | null;
  result?: Record<string, unknown> | null;
};

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function parseError(response: Response): Promise<ApiError> {
  let message = 'Something went wrong. Please try again.';
  let code: string | null = null;
  try {
    const payload = await response.json() as { detail?: unknown; message?: unknown };
    const detail = payload.detail;
    if (typeof detail === 'string' && detail.trim()) message = detail;
    if (detail && typeof detail === 'object') {
      const data = detail as { message?: unknown; error?: unknown };
      if (typeof data.message === 'string' && data.message.trim()) message = data.message;
      if (typeof data.error === 'string' && data.error.trim()) code = data.error;
    } else if (typeof payload.message === 'string' && payload.message.trim()) {
      message = payload.message;
    }
  } catch {
    // Keep the stable user-facing fallback for non-JSON failures.
  }
  return new ApiError(message, response.status, code);
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getStoredToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await fetch(`${apiBase()}${path}`, { ...init, headers });
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}

export async function loginWithPassword(email: string, password: string): Promise<AccountProfile> {
  const body = new URLSearchParams({ username: email.trim(), password });
  const response = await fetch(`${apiBase()}/login/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) throw await parseError(response);
  const payload = await response.json() as { access_token?: unknown };
  const token = String(payload.access_token || '').trim();
  if (!token) throw new ApiError('Sign in could not be completed.', 500);
  storeToken(token);
  try {
    return await getProfile();
  } catch (error) {
    clearToken();
    throw error;
  }
}

function acceptAuthResponse(payload: AuthResponse): AccountProfile {
  const token = String(payload.access_token || '').trim();
  if (!token || !payload.user) throw new ApiError('Account access could not be completed.', 500);
  storeToken(token);
  return payload.user;
}

export function sendVerificationCode(email: string): Promise<{ expires_in_seconds: number }> {
  return apiFetch('/auth/send-verification-code', {
    method: 'POST',
    body: JSON.stringify({ email: email.trim().toLowerCase(), language: 'en', brand: 'sanjeom' }),
  });
}

export async function registerWithCode(input: {
  email: string;
  code: string;
  password: string;
}): Promise<AccountProfile> {
  const response = await apiFetch<AuthResponse>('/auth/register-with-code', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email.trim().toLowerCase(),
      code: input.code.trim(),
      password: input.password,
      language: 'en',
      accept_terms: true,
      accept_privacy: true,
      accept_ai_data_sharing: true,
      ai_data_sharing_version: CURRENT_AI_DATA_SHARING_VERSION,
      ai_data_sharing_locale: 'en',
    }),
  });
  return acceptAuthResponse(response);
}

export async function resetPasswordWithCode(input: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<AccountProfile> {
  const verification = await apiFetch<{ password_reset_token?: unknown }>('/auth/login-with-code', {
    method: 'POST',
    body: JSON.stringify({ email: input.email.trim().toLowerCase(), code: input.code.trim() }),
  });
  const passwordResetToken = String(verification.password_reset_token || '').trim();
  if (!passwordResetToken) throw new ApiError('Password reset could not be completed.', 500);
  const response = await apiFetch<AuthResponse>('/auth/complete-email-code-password-reset', {
    method: 'POST',
    body: JSON.stringify({ password_reset_token: passwordResetToken, new_password: input.newPassword }),
  });
  return acceptAuthResponse(response);
}

export function getProfile(): Promise<AccountProfile> {
  return apiFetch<AccountProfile>('/users/me');
}

export function getQuestionBalance(): Promise<{ questions_remaining: number }> {
  return apiFetch('/sanjeom-ai/wallet');
}

export function getActiveTurn(): Promise<{
  session_id: string;
  turn_id: string;
  status: 'queued' | 'running';
  message: string;
} | null> {
  return apiFetch('/sanjeom-ai/active-turn');
}

export async function listConversations(): Promise<ConversationSummary[]> {
  const response = await apiFetch<{ items: ConversationSummary[] }>('/sanjeom-ai/sessions?limit=50');
  return Array.isArray(response.items) ? response.items : [];
}

export function getConversation(sessionId: string): Promise<Conversation> {
  return apiFetch(`/sanjeom-ai/sessions/${encodeURIComponent(sessionId)}`);
}

export function deleteConversation(sessionId: string): Promise<void> {
  return apiFetch(`/sanjeom-ai/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
}

export function submitQuestion(input: {
  sessionId?: string | null;
  message: string;
  clientTurnKey: string;
}): Promise<{ session_id: string; turn_id: string; status: TurnStatus['status'] }> {
  return apiFetch('/sanjeom-ai/turns', {
    method: 'POST',
    body: JSON.stringify({
      session_id: input.sessionId || '',
      message: input.message,
      language: 'en',
      mode: 'default',
      client_turn_key: input.clientTurnKey,
    }),
  });
}

export function getTurnStatus(sessionId: string, turnId: string): Promise<TurnStatus> {
  return apiFetch(
    `/sanjeom-ai/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(turnId)}`,
  );
}
